
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "apache-atlas-hook--bridge-for-apache-hbase"
    }}>{`Apache Atlas Hook & Bridge for Apache HBase`}</h1>
    <h2 {...{
      "id": "hbase-model"
    }}>{`HBase Model`}</h2>
    <p>{`HBase model includes the following types:`}</p>
    <ul>
      <li parentName="ul">{`Entity types:`}
        <ul parentName="li">
          <li parentName="ul">{`hbase_namespace`}
            <ul parentName="li">
              <li parentName="ul">{`super-types: Asset`}</li>
              <li parentName="ul">{`attributes: qualifiedName, name, description, owner, clusterName, parameters, createTime, modifiedTime`}</li>
            </ul>
          </li>
          <li parentName="ul">{`hbase_table`}
            <ul parentName="li">
              <li parentName="ul">{`super-types: DataSet`}</li>
              <li parentName="ul">{`attributes: qualifiedName, name, description, owner, namespace, column_families, uri, parameters, createtime, modifiedtime, maxfilesize, isReadOnly, isCompactionEnabled, isNormalizationEnabled, ReplicaPerRegion, Durability`}</li>
            </ul>
          </li>
          <li parentName="ul">{`hbase_column_family`}
            <ul parentName="li">
              <li parentName="ul">{`super-types: DataSet`}</li>
              <li parentName="ul">{`attributes:  qualifiedName, name, description, owner, columns, createTime, bloomFilterType, compressionType, compactionCompressionType, encryptionType, inMemoryCompactionPolicy, keepDeletedCells, maxversions, minVersions, datablockEncoding, storagePolicy, ttl, blockCachedEnabled, cacheBloomsOnWrite, cacheDataOnWrite, evictBlocksOnClose, prefetchBlocksOnOpen, newVersionsBehavior, isMobEnabled, mobCompactPartitionPolicy`}</li>
            </ul>
          </li>
        </ul>
      </li>
    </ul>
    <p>{`HBase entities are created and de-duped in Atlas using unique attribute qualifiedName, whose value should be formatted as detailed below. Note that namespaceName, tableName and columnFamilyName should be in lower case.`}</p>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
      {`hbase_namespace.qualifiedName:      <namespaceName>@<clusterName>
hbase_table.qualifiedName:          <namespaceName>:<tableName>@<clusterName>
hbase_column_family.qualifiedName:  <namespaceName>:<tableName>.<columnFamilyName>@<clusterName>`}
    </SyntaxHighlighter>
    <h2 {...{
      "id": "hbase-hook"
    }}>{`HBase Hook`}</h2>
    <p>{`Atlas HBase hook registers with HBase master as a co-processor. On detecting changes to HBase namespaces/tables/column-families, Atlas hook updates the metadata in Atlas via Kafka notifications.
Follow the instructions below to setup Atlas hook in HBase:`}</p>
    <ul>
      <li parentName="ul">{`Register Atlas hook in hbase-site.xml by adding the following:`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="xml" style={theme.dark} mdxType="SyntaxHighlighter">
      {`<property>
<name>hbase.coprocessor.master.classes</name>
<value>org.apache.atlas.hbase.hook.HBaseAtlasCoprocessor</value>
</property>`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`untar apache-atlas-\${project.version}-hbase-hook.tar.gz`}</li>
      <li parentName="ul">{`cd apache-atlas-hbase-hook-\${project.version}`}</li>
      <li parentName="ul">{`Copy entire contents of folder apache-atlas-hbase-hook-\${project.version}/hook/hbase to `}<inlineCode parentName="li">{`<atlas package>`}</inlineCode>{`/hook/hbase`}</li>
      <li parentName="ul">{`Link Atlas hook jars in HBase classpath - 'ln -s `}<inlineCode parentName="li">{`<atlas package>`}</inlineCode>{`/hook/hbase/* `}<inlineCode parentName="li">{`<hbase-home>`}</inlineCode>{`/lib/'`}</li>
      <li parentName="ul">{`Copy `}<inlineCode parentName="li">{`<atlas-conf>`}</inlineCode>{`/atlas-application.properties to the HBase conf directory.`}</li>
    </ul>
    <p>{`The following properties in atlas-application.properties control the thread pool and notification details:`}</p>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.hook.hbase.synchronous=false # whether to run the hook synchronously. false is recommended to avoid delays in HBase operations. Default: false
atlas.hook.hbase.numRetries=3      # number of retries for notification failure. Default: 3
atlas.hook.hbase.queueSize=10000   # queue size for the threadpool. Default: 10000
atlas.cluster.name=primary # clusterName to use in qualifiedName of entities. Default: primary
atlas.kafka.zookeeper.connect=                    # Zookeeper connect URL for Kafka. Example: localhost:2181
atlas.kafka.zookeeper.connection.timeout.ms=30000 # Zookeeper connection timeout. Default: 30000
atlas.kafka.zookeeper.session.timeout.ms=60000    # Zookeeper session timeout. Default: 60000
atlas.kafka.zookeeper.sync.time.ms=20             # Zookeeper sync time. Default: 20`}
    </SyntaxHighlighter>
    <p>{`Other configurations for Kafka notification producer can be specified by prefixing the configuration name with "atlas.kafka.".
For list of configuration supported by Kafka producer, please refer to `}<a parentName="p" {...{
        "href": "http://kafka.apache.org/documentation/#producerconfigs"
      }}>{`Kafka Producer Configs`}</a></p>
    <h2 {...{
      "id": "notes"
    }}>{`NOTES`}</h2>
    <ul>
      <li parentName="ul">{`Only the namespace, table and column-family create/update/delete operations are captured by Atlas HBase hook. Changes to columns are be captured.`}</li>
    </ul>
    <h2 {...{
      "id": "importing-hbase-metadata"
    }}>{`Importing HBase Metadata`}</h2>
    <p>{`Apache Atlas provides a command-line utility, import-hbase.sh, to import metadata of Apache HBase namespaces and tables into Apache Atlas.
This utility can be used to initialize Apache Atlas with namespaces/tables present in an Apache HBase cluster.
This utility supports importing metadata of a specific table, tables in a specific namespace or all tables.`}</p>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Usage 1: <atlas package>/hook-bin/import-hbase.sh
Usage 2: <atlas package>/hook-bin/import-hbase.sh [-n <namespace regex> OR --namespace <namespace regex>] [-t <table regex> OR --table <table regex>]
Usage 3: <atlas package>/hook-bin/import-hbase.sh [-f <filename>]
           File Format:
             namespace1:tbl1
             namespace1:tbl2
             namespace2:tbl1`}
    </SyntaxHighlighter>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;